import { normalizeChecklistDefaultValue } from "../registry/fields/checklist/normalizeDefault";
import { normalizeDateDefaultValue } from "../registry/fields/date/normalizeDefault";
import { normalizeDateRangeDefaultValue } from "../registry/fields/date-range/normalizeDefault";
import { normalizeDueDateDefaultValue } from "../registry/fields/due-date/normalizeDefault";
import { normalizeLinkedRecordDefaultValue } from "../registry/fields/linked-record/normalizeDefault";
import { normalizeNumberDefaultValue } from "../registry/fields/number/normalizeDefault";
import { normalizeSelectDefaultValue } from "../registry/fields/single-select/normalizeDefault";
import { normalizeYesNoDefaultValue } from "../registry/fields/yes-no/normalizeDefault";
import { Fields } from "../types/fields";
import {
  type CurrentUser,
  type FieldChoice,
  type FormConfig,
  type FormConfigApplicationStructureItem,
  type FormConfigItem,
  FormItemType,
} from "../types/form";
import { type OriginatingRecord, resolveOriginatingRecordMarker } from "./originatingRecord";

export const FORM_DEFAULT_SUPPORTED_FIELDS = new Set<Fields>([
  Fields.textfield,
  Fields.textareafield,
  Fields.numberfield,
  Fields.currencyfield,
  Fields.percentfield,
  Fields.numbersliderfield,
  Fields.percentcompletefield,
  Fields.ratingfield,
  Fields.singleselectfield,
  Fields.multipleselectfield,
  Fields.statusfield,
  Fields.tagsfield,
  Fields.userfield,
  Fields.datefield,
  Fields.duedatefield,
  Fields.daterangefield,
  Fields.linkedrecordfield,
  Fields.yesnofield,
  Fields.checklistfield,
]);

const CHOICE_BASED_FIELDS = new Set<Fields>([
  Fields.singleselectfield,
  Fields.multipleselectfield,
  Fields.statusfield,
  Fields.tagsfield,
]);

const CURRENT_USER_MARKER = "current";

export interface DefaultNormalizerContext {
  timezone: string;
  includeTime: boolean;
}

// `any` for the value: each normalizer takes its own field's raw default, and a narrower
// parameter type here would reject them under strictFunctionTypes.
type DefaultNormalizer = (value: any, context: DefaultNormalizerContext) => unknown;

const defaultNormalizers = new Map<Fields, DefaultNormalizer>([
  [Fields.singleselectfield, normalizeSelectDefaultValue],
  [Fields.statusfield, normalizeSelectDefaultValue],
  [Fields.datefield, normalizeDateDefaultValue],
  [Fields.duedatefield, normalizeDueDateDefaultValue],
  [Fields.daterangefield, normalizeDateRangeDefaultValue],
  [Fields.numberfield, normalizeNumberDefaultValue],
  [Fields.currencyfield, normalizeNumberDefaultValue],
  [Fields.percentfield, normalizeNumberDefaultValue],
  [Fields.numbersliderfield, normalizeNumberDefaultValue],
  [Fields.percentcompletefield, normalizeNumberDefaultValue],
  [Fields.ratingfield, normalizeNumberDefaultValue],
  [Fields.linkedrecordfield, normalizeLinkedRecordDefaultValue],
  [Fields.yesnofield, normalizeYesNoDefaultValue],
  [Fields.checklistfield, normalizeChecklistDefaultValue],
]);

export interface FormDefaultValues {
  simpleFields: Record<string, unknown>;
  dynamicUserFields: Record<string, string[]>;
}

const collectFieldItems = (items: FormConfigItem[]): FormConfigItem[] =>
  items.flatMap((item) => {
    if (item.type === FormItemType.section && item.params.items) {
      return item.params.items;
    }
    return [item];
  });

/**
 * The default a form item actually applies: its own when set, otherwise the field's. Exported
 * because the checklist control has to resolve it the same way — react-ui identifies locked
 * default items by id, so it must be handed the very items this seeds.
 */
export const resolveDefaultValue = (
  item: FormConfigItem,
  field: FormConfigApplicationStructureItem,
): unknown => {
  if (item.params.default_value !== undefined) {
    return item.params.default_value;
  }
  // Fall back to field-level default from application structure
  const fieldParams = field.params as Record<string, unknown>;
  return fieldParams.default_value;
};

const currentUserToMember = (
  user: CurrentUser,
): { id: string; name: string; member: { id: string; name: string } } => ({
  id: user.id,
  name: user.full_name.sys_root,
  member: { id: user.id, name: user.full_name.sys_root },
});

const resolveUserFieldDefault = (
  slug: string,
  value: unknown,
  result: FormDefaultValues,
  currentUser?: CurrentUser,
): void => {
  if (value === CURRENT_USER_MARKER) {
    if (currentUser) {
      result.simpleFields[slug] = [currentUserToMember(currentUser)];
    }
    return;
  }

  if (Array.isArray(value)) {
    result.dynamicUserFields[slug] = value as string[];
  }
};

const validateChoiceDefault = (value: unknown, choices: FieldChoice[]): unknown => {
  const validValues = new Set(choices.map((c) => c.value));

  if (Array.isArray(value)) {
    const filtered = value.filter((v) => typeof v === "string" && validValues.has(v));
    return filtered.length > 0 ? filtered : undefined;
  }

  if (typeof value === "string") {
    return validValues.has(value) ? value : undefined;
  }

  return undefined;
};

const processFieldItem = (
  item: FormConfigItem,
  structureMap: Map<string, FormConfigApplicationStructureItem>,
  result: FormDefaultValues,
  config: FormConfig,
  timezone: string,
  originatingRecord: OriginatingRecord | undefined,
): void => {
  if (item.type !== FormItemType.field || !item.params.default_value_enabled) {
    return;
  }

  const field = structureMap.get(item.slug);
  if (!field || !FORM_DEFAULT_SUPPORTED_FIELDS.has(field.field_type)) {
    return;
  }

  if (field.field_type === Fields.linkedrecordfield && !config.feature_flags?.lr_default_value) {
    return;
  }

  if (field.field_type === Fields.yesnofield && !config.feature_flags?.form_yesno_default_value) {
    return;
  }

  const rawValue = resolveDefaultValue(item, field);
  if (rawValue == null) {
    return;
  }

  if (field.field_type === Fields.userfield) {
    resolveUserFieldDefault(item.slug, rawValue, result, config.current_user);
    return;
  }

  const normalizer = defaultNormalizers.get(field.field_type);
  let normalized = normalizer
    ? normalizer(rawValue, { timezone, includeTime: item.params.include_time === true })
    : rawValue;

  if (CHOICE_BASED_FIELDS.has(field.field_type) && field.params.choices) {
    normalized = validateChoiceDefault(normalized, field.params.choices);
  }

  if (field.field_type === Fields.linkedrecordfield) {
    normalized = resolveOriginatingRecordMarker(
      normalized as string[],
      field.params.linked_application,
      config.feature_flags?.lr_originating_record_default ? originatingRecord : undefined,
    );
  }

  if (normalized !== undefined) {
    result.simpleFields[item.slug] = normalized;
  }
};

/**
 * Builds the initial form values from the form's default values. `originatingRecord` is the record
 * a Button opened the form from; it resolves the Linked Record "Originating Record" default.
 */
export const getFormDefaultValues = (
  config: FormConfig,
  timezone: string,
  originatingRecord?: OriginatingRecord,
): FormDefaultValues => {
  const result: FormDefaultValues = {
    simpleFields: {},
    dynamicUserFields: {},
  };

  const structureMap = new Map(config.application.structure.map((f) => [f.slug, f]));
  const fieldItems = collectFieldItems(config.form_state.items);

  for (const item of fieldItems) {
    processFieldItem(item, structureMap, result, config, timezone, originatingRecord);
  }

  return result;
};
